import { useState, useEffect, useCallback } from 'react'
import { useRouter } from '@tanstack/react-router'
import { Command } from 'cmdk'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useGlobalSearch } from '@/api/hooks'
import {
  Search,
  Loader2,
  FileText,
  FolderOpen,
  Users,
  UserCog,
  Factory,
  Box,
  Receipt,
} from 'lucide-react'
import type { components } from '@/api/schema'

type SearchResultItem = components['schemas']['GlobalSearchResultItem']
type SearchResponse = components['schemas']['GlobalSearchResponse']

const ENTITY_GROUPS = [
  {
    key: 'projects' as const,
    label: 'Projects',
    icon: FolderOpen,
    color: 'text-violet-500',
    route: '/projects/$projectId',
    paramKey: 'projectId',
  },
  {
    key: 'purchase_orders' as const,
    label: 'Purchase Orders',
    icon: FileText,
    color: 'text-emerald-500',
    route: '/purchase-orders/$poId',
    paramKey: 'poId',
  },
  {
    key: 'invoices' as const,
    label: 'AP Invoices',
    icon: Receipt,
    color: 'text-sky-500',
    route: '/invoices/$invoiceId',
    paramKey: 'invoiceId',
  },
  {
    key: 'customer_groups' as const,
    label: 'Customers',
    icon: Users,
    color: 'text-amber-500',
    route: '/customers/$groupId',
    paramKey: 'groupId',
  },
  {
    key: 'manufacturers' as const,
    label: 'Manufacturers',
    icon: Factory,
    color: 'text-pink-500',
    route: '/manufacturers/$manuId',
    paramKey: 'manuId',
  },
  {
    key: 'staff' as const,
    label: 'Staff',
    icon: UserCog,
    color: 'text-cyan-500',
    route: '/staff/$staffId',
    paramKey: 'staffId',
  },
  {
    key: 'products' as const,
    label: 'Products',
    icon: Box,
    color: 'text-orange-500',
    route: '/products/$productId',
    paramKey: 'productId',
  },
] as const

export function CommandMenu() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 150)
  const router = useRouter()
  const { data, isLoading, isFetching } = useGlobalSearch(debouncedQuery)

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(true)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // Reset query when dialog closes — wrap setOpen to avoid setState-in-effect
  const setOpenAndReset = useCallback((next: boolean) => {
    setOpen(next)
    if (!next) setQuery('')
  }, [])

  const handleSelect = useCallback(
    (route: string, paramKey: string, uuid: string) => {
      setOpenAndReset(false)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      void router.navigate({ to: route as any, params: { [paramKey]: uuid } as any })
    },
    [router, setOpenAndReset],
  )

  const showLoading = isFetching && debouncedQuery.length >= 2
  const hasQuery = debouncedQuery.length >= 2

  // Collect groups that have results
  const groups: Array<{
    key: string
    label: string
    icon: typeof FolderOpen
    color: string
    route: string
    paramKey: string
    items: SearchResultItem[]
  }> = []

  if (data && hasQuery) {
    for (const group of ENTITY_GROUPS) {
      const items = (data as SearchResponse)[group.key]
      if (items && items.length > 0) {
        groups.push({ ...group, items })
      }
    }
  }

  const hasResults = groups.length > 0

  return (
    <Dialog open={open} onOpenChange={setOpenAndReset}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="relative flex h-9 w-full max-w-lg items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/60"
        >
          <Search className="mr-2 h-4 w-4 shrink-0" />
          <span className="flex-1 text-left">Search...</span>
          <kbd className="pointer-events-none ml-auto hidden h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>
      </DialogTrigger>

        <DialogContent
          className="overflow-hidden p-0 sm:max-w-lg"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">Search</DialogTitle>
          <Command shouldFilter={false} className="flex flex-col">
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Search projects, POs, invoices, customers..."
                className="flex h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              {showLoading && (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
              )}
            </div>
            <Command.List className="max-h-[340px] overflow-y-auto p-1.5">
              {hasQuery && !isLoading && !hasResults && (
                <Command.Empty className="py-8 text-center text-sm text-muted-foreground">
                  No results found.
                </Command.Empty>
              )}
              {!hasQuery && (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  Start typing to search across all entities...
                </div>
              )}
              {groups.map((group) => {
                const Icon = group.icon
                return (
                  <Command.Group
                    key={group.key}
                    heading={group.label}
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground"
                  >
                    {group.items.map((item) => (
                      <Command.Item
                        key={`${item.entity_type}-${item.entity_uuid}`}
                        value={`${item.entity_type}-${item.entity_uuid}`}
                        onSelect={() =>
                          handleSelect(group.route, group.paramKey, item.entity_uuid)
                        }
                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm aria-selected:bg-accent aria-selected:text-accent-foreground"
                      >
                        <Icon className={`h-4 w-4 shrink-0 ${group.color}`} />
                        <span className="flex-1 truncate font-medium">
                          {item.display_text}
                        </span>
                        {item.secondary_text && (
                          <span className="truncate text-xs text-muted-foreground">
                            {item.secondary_text}
                          </span>
                        )}
                      </Command.Item>
                    ))}
                  </Command.Group>
                )
              })}
            </Command.List>
          </Command>
        </DialogContent>
    </Dialog>
  )
}
